
import React, { useState } from 'react';
import { PortfolioItem } from './types';
import { Icon } from './components/Icon';
import { APP_SETTINGS } from './constants';
import { OrderModal } from './components/OrderModal';

interface PortfolioWebsiteProps {
  items: PortfolioItem[];
  onBack: () => void;
  contactNumber?: string; // Menambahkan prop contactNumber, optional karena default bisa handle
}

const PortfolioWebsite: React.FC<PortfolioWebsiteProps> = ({ items, onBack, contactNumber = APP_SETTINGS.contactWhatsapp }) => {
  const websiteItems = items.filter(item => item.category === 'website');
  const [isOrderOpen, setIsOrderOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('');

  const handleOrder = (pkgName: string) => {
    setSelectedPackage(pkgName);
    setIsOrderOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#030712] pt-32 pb-20">
      <OrderModal 
        isOpen={isOrderOpen} 
        onClose={() => setIsOrderOpen(false)} 
        selectedPackage={selectedPackage} 
        contactNumber={contactNumber}
      />

      <div className="container mx-auto px-6">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors group"
        >
          <div className="p-2 glass-morphism rounded-lg group-hover:bg-white/10">
            <Icon name="ArrowRight" className="rotate-180" size={18} />
          </div>
          <span>Kembali ke Beranda</span>
        </button>

        {/* Portfolio Section */}
        <div className="mb-16">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">Portofolio <span className="gradient-text">Website</span></h1>
          <p className="text-slate-400 max-w-2xl">Kumpulan karya website profesional yang telah kami bangun untuk berbagai industri.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-32">
          {websiteItems.length > 0 ? websiteItems.map((item) => (
            <div key={item.id} className="glass-morphism rounded-3xl overflow-hidden border border-white/10 hover:border-purple-500/50 transition-all group">
              <div className="aspect-video relative overflow-hidden">
                <img 
                  src={item.imageUrl || `https://picsum.photos/seed/${item.id}/600/400`} 
                  alt={item.title} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                   <a 
                    href={item.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white text-black px-4 py-2 rounded-full font-bold text-sm"
                   >
                     Kunjungi Situs <Icon name="ExternalLink" size={14} />
                   </a>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm mb-4 line-clamp-2">{item.description}</p>
                <a 
                  href={item.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-purple-400 font-semibold text-sm hover:underline flex items-center gap-1"
                >
                  Detail Project <Icon name="ChevronRight" size={14} />
                </a>
              </div>
            </div>
          )) : (
            <div className="col-span-full py-20 text-center glass-morphism rounded-3xl border border-dashed border-white/20">
              <p className="text-slate-500">Belum ada portofolio website yang ditambahkan.</p>
            </div>
          )}
        </div>

        {/* Pricing & Packages Section */}
        <div className="mb-12">
            <div className="text-center mb-16 max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-morphism mb-6 border border-purple-500/30">
                    <Icon name="Zap" size={16} className="text-purple-400" />
                    <span className="text-xs font-bold text-purple-300 tracking-wider uppercase">Teknologi Modern SPA</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-extrabold mb-6 text-white">Bukan Sekadar Website Biasa</h2>
                <p className="text-slate-400 leading-relaxed text-lg">
                    Website kami dibangun menggunakan teknologi <strong>Single Page Application (SPA)</strong>. 
                    Perpindahan antar menu terjadi secara instan tanpa reload halaman, memberikan pengalaman pengguna 
                    yang jauh lebih cepat, profesional, dan modern dibandingkan website konvensional.
                </p>
            </div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
                {/* Package 1 */}
                <div className="glass-morphism rounded-3xl p-8 border border-white/10 flex flex-col hover:border-blue-500/50 transition-all relative">
                    <div className="mb-6">
                        <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
                            <Icon name="Layout" className="text-blue-400" size={24} />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">CORE PRESENCE</h3>
                        <p className="text-slate-400 text-xs uppercase font-bold tracking-widest">Paket Ekonomis</p>
                    </div>
                    <div className="mb-6">
                        <span className="text-3xl font-extrabold text-white">Rp 750rb</span>
                    </div>
                    <p className="text-sm text-slate-400 mb-6">
                        Fondasi digital yang kokoh. Cocok untuk Personal Branding, UMKM, atau Portofolio.
                    </p>
                    <ul className="space-y-3 mb-8 flex-1">
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-blue-500" /> Modern SPA Tech</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-blue-500" /> Responsive Design</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-blue-500" /> Hero & About Section</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-blue-500" /> Galeri & Artikel</li>
                    </ul>
                    <button onClick={() => handleOrder('Web - Core Presence')} className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl font-bold text-sm text-center transition-all">Pilih Paket Core</button>
                </div>

                {/* Package 2 */}
                <div className="glass-morphism rounded-3xl p-8 border border-purple-500/50 bg-gradient-to-b from-purple-500/5 to-transparent flex flex-col relative transform md:-translate-y-4">
                    <div className="absolute top-0 right-0 bg-purple-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl rounded-tr-2xl">POPULAR</div>
                    <div className="mb-6">
                        <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                            <Icon name="Database" className="text-purple-400" size={24} />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">INTERACT FLOW</h3>
                        <p className="text-purple-300 text-xs uppercase font-bold tracking-widest">Paket Pertengahan</p>
                    </div>
                    <div className="mb-6">
                        <span className="text-3xl font-extrabold text-white">Rp 1.5jt</span>
                    </div>
                    <p className="text-sm text-slate-400 mb-6">
                        Ubah pengunjung menjadi klien. Cocok untuk PPDB Sekolah, Booking Jasa, atau Event.
                    </p>
                    <ul className="space-y-3 mb-8 flex-1">
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-purple-500" /> <strong>Semua Fitur Core</strong></li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-purple-500" /> Form Pendaftaran/Kontak</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-purple-500" /> Validasi Data Real-time</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-purple-500" /> Integrasi Email/WA</li>
                    </ul>
                    <button onClick={() => handleOrder('Web - Interact Flow')} className="w-full py-3 bg-purple-600 hover:bg-purple-700 rounded-xl font-bold text-sm text-center text-white shadow-lg shadow-purple-500/20 transition-all">Pilih Paket Flow</button>
                </div>

                {/* Package 3 */}
                <div className="glass-morphism rounded-3xl p-8 border border-yellow-500/20 flex flex-col hover:border-yellow-500/50 transition-all relative">
                    <div className="mb-6">
                        <div className="w-12 h-12 bg-yellow-500/10 rounded-xl flex items-center justify-center mb-4">
                            <Icon name="Bot" className="text-yellow-400" size={24} />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">INTELLIGENT EDGE</h3>
                        <p className="text-slate-400 text-xs uppercase font-bold tracking-widest">Paket Advantage</p>
                    </div>
                    <div className="mb-6">
                        <span className="text-3xl font-extrabold text-white">Rp 3jt</span>
                    </div>
                    <p className="text-sm text-slate-400 mb-6">
                        Solusi High-End dengan kecerdasan buatan. Untuk Startup & Institusi Maju.
                    </p>
                    <ul className="space-y-3 mb-8 flex-1">
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-yellow-500" /> <strong>Semua Fitur Flow</strong></li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-yellow-500" /> AI Chatbot Integration</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-yellow-500" /> Sistem User Login</li>
                        <li className="flex items-center gap-3 text-sm text-slate-300"><Icon name="CheckCircle2" size={16} className="text-yellow-500" /> Member Area Dashboard</li>
                    </ul>
                    <button onClick={() => handleOrder('Web - Intelligent Edge')} className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl font-bold text-sm text-center transition-all">Pilih Paket Edge</button>
                </div>
            </div>

            {/* Comparison Table */}
            <div className="glass-morphism rounded-3xl overflow-hidden border border-white/10 p-6 md:p-10">
                <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-white mb-2">Perbandingan Fitur Lengkap</h3>
                    <p className="text-slate-400 text-sm">Detail spesifikasi teknis untuk setiap paket.</p>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr>
                                <th className="p-4 text-sm font-bold text-slate-500 uppercase tracking-widest border-b border-white/10">Fitur</th>
                                <th className="p-4 text-sm font-bold text-blue-400 uppercase tracking-widest border-b border-white/10 text-center">Core Presence</th>
                                <th className="p-4 text-sm font-bold text-purple-400 uppercase tracking-widest border-b border-white/10 text-center bg-purple-500/5">Interact Flow</th>
                                <th className="p-4 text-sm font-bold text-yellow-400 uppercase tracking-widest border-b border-white/10 text-center">Intelligent Edge</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm text-slate-300">
                            {[
                                { name: 'Technology', v1: 'Modern SPA', v2: 'Modern SPA', v3: 'Modern SPA + AI' },
                                { name: 'Pengerjaan', v1: '3-5 Hari', v2: '7-14 Hari', v3: '10-30 Hari' },
                                { name: 'Responsive Design', v1: true, v2: true, v3: true },
                                { name: 'Halaman Berita/Artikel', v1: true, v2: true, v3: true },
                                { name: 'Form Kontak/Pendaftaran', v1: false, v2: true, v3: true },
                                { name: 'Validasi Input Real-time', v1: false, v2: true, v3: true },
                                { name: 'User Login / Auth', v1: false, v2: false, v3: true },
                                { name: 'AI Chatbot Assistant', v1: false, v2: false, v3: true },
                                { name: 'Dashboard Member', v1: false, v2: false, v3: true },
                            ].map((row, idx) => (
                                <tr key={idx} className="hover:bg-white/5 transition-colors">
                                    <td className="p-4 border-b border-white/5 font-medium">{row.name}</td>
                                    <td className="p-4 border-b border-white/5 text-center">
                                        {row.v1 === true ? <Icon name="CheckCircle2" size={18} className="text-blue-500 mx-auto" /> : row.v1 === false ? <Icon name="X" size={18} className="text-slate-700 mx-auto" /> : <span className="font-bold text-white">{row.v1}</span>}
                                    </td>
                                    <td className="p-4 border-b border-white/5 text-center bg-purple-500/5">
                                        {row.v2 === true ? <Icon name="CheckCircle2" size={18} className="text-purple-500 mx-auto" /> : row.v2 === false ? <Icon name="X" size={18} className="text-slate-700 mx-auto" /> : <span className="font-bold text-white">{row.v2}</span>}
                                    </td>
                                    <td className="p-4 border-b border-white/5 text-center">
                                        {row.v3 === true ? <Icon name="CheckCircle2" size={18} className="text-yellow-500 mx-auto" /> : row.v3 === false ? <Icon name="X" size={18} className="text-slate-700 mx-auto" /> : <span className="font-bold text-white">{row.v3}</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioWebsite;
